/**
 * Subscription lifecycle: statuses, allowed transitions, dunning escalation
 * and reminder windows. Time is passed in as epoch milliseconds so the module
 * stays deterministic and testable.
 */

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'degraded',
  'suspended',
  'canceled',
  'expired',
] as const;
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

/** Statuses where the banker keeps operating, even if with reduced features. */
const OPERATING: readonly SubscriptionStatus[] = ['trialing', 'active', 'past_due', 'degraded'];

/** Statuses where only billing actions are available. */
const READ_ONLY: readonly SubscriptionStatus[] = ['suspended', 'canceled', 'expired'];

const TRANSITIONS: Record<SubscriptionStatus, readonly SubscriptionStatus[]> = {
  trialing: ['active', 'canceled', 'expired', 'past_due'],
  active: ['past_due', 'canceled', 'expired'],
  past_due: ['active', 'degraded', 'canceled', 'expired'],
  degraded: ['active', 'suspended', 'canceled', 'expired'],
  suspended: ['active', 'canceled', 'expired'],
  canceled: ['active', 'expired'],
  expired: ['active'],
};

export interface DunningPolicy {
  /** Days in `past_due` with full access before features are reduced. */
  graceDays: number;
  /** Days in `past_due` before the account is suspended. */
  suspendDays: number;
}

export const DEFAULT_DUNNING_POLICY: DunningPolicy = {
  graceDays: 7,
  suspendDays: 14,
};

/** Days before period end that trigger a renewal reminder. */
export const RENEWAL_REMINDER_DAYS: readonly number[] = [7, 3, 1];

/** Days before trial end that trigger a conversion reminder. */
export const TRIAL_REMINDER_DAYS: readonly number[] = [3, 1, 0];

/** Days in `past_due` that trigger a follow-up dunning notice. */
export const DUNNING_REMINDER_DAYS: readonly number[] = [0, 3, 7];

export const DAY_MS = 24 * 60 * 60 * 1000;

export function isSubscriptionStatus(value: unknown): value is SubscriptionStatus {
  return typeof value === 'string'
    && (SUBSCRIPTION_STATUSES as readonly string[]).includes(value);
}

/** Entitled statuses still resolve features (degraded resolves a reduced set). */
export function isEntitledStatus(status: SubscriptionStatus): boolean {
  return OPERATING.includes(status);
}

export function isReadOnlyStatus(status: SubscriptionStatus): boolean {
  return READ_ONLY.includes(status);
}

/** Whether the banker owes money right now. */
export function isDelinquentStatus(status: SubscriptionStatus): boolean {
  return status === 'past_due' || status === 'degraded' || status === 'suspended';
}

export function canTransition(from: SubscriptionStatus, to: SubscriptionStatus): boolean {
  if (from === to) return true;
  return TRANSITIONS[from].includes(to);
}

/** Whole days elapsed between two instants, never negative. */
export function daysElapsed(fromMs: number, toMs: number): number {
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return 0;
  return Math.max(0, Math.floor((toMs - fromMs) / DAY_MS));
}

/** Whole days remaining until an instant, never negative. */
export function daysUntil(targetMs: number, nowMs: number): number {
  if (!Number.isFinite(targetMs) || !Number.isFinite(nowMs)) return 0;
  return Math.max(0, Math.ceil((targetMs - nowMs) / DAY_MS));
}

export function trialEndsAtMs(startMs: number, trialDays: number): number {
  return startMs + Math.max(0, Math.trunc(trialDays)) * DAY_MS;
}

/**
 * Escalate an unpaid subscription. Returns the status it should hold now,
 * which may be the same one it already has.
 */
export function nextDunningStatus(
  status: SubscriptionStatus,
  pastDueSinceMs: number | null | undefined,
  nowMs: number,
  policy: DunningPolicy = DEFAULT_DUNNING_POLICY,
): SubscriptionStatus {
  if (status !== 'past_due' && status !== 'degraded') return status;
  if (!pastDueSinceMs || !Number.isFinite(pastDueSinceMs)) return status;
  const elapsed = daysElapsed(pastDueSinceMs, nowMs);
  if (elapsed >= policy.suspendDays) return 'suspended';
  if (elapsed >= policy.graceDays) return 'degraded';
  return 'past_due';
}

/** Day offset whose reminder is due now, or null when none matches. */
export function dueReminderOffset(
  targetMs: number | null | undefined,
  nowMs: number,
  offsets: readonly number[],
): number | null {
  if (!targetMs || !Number.isFinite(targetMs)) return null;
  const remaining = daysUntil(targetMs, nowMs);
  for (const offset of offsets) {
    if (remaining === offset) return offset;
  }
  return null;
}

/** Dunning follow-up due for an unpaid subscription, or null when none. */
export function dueDunningOffset(
  pastDueSinceMs: number | null | undefined,
  nowMs: number,
  offsets: readonly number[] = DUNNING_REMINDER_DAYS,
): number | null {
  if (!pastDueSinceMs || !Number.isFinite(pastDueSinceMs)) return null;
  const elapsed = daysElapsed(pastDueSinceMs, nowMs);
  for (const offset of offsets) {
    if (elapsed === offset) return offset;
  }
  return null;
}

/** A reminder already sent within the same day must not be repeated. */
export function shouldSendReminder(
  lastSentAtMs: number | null | undefined,
  nowMs: number,
): boolean {
  if (!lastSentAtMs || !Number.isFinite(lastSentAtMs)) return true;
  return daysElapsed(lastSentAtMs, nowMs) >= 1;
}

/**
 * A trial that ends without a payment arrangement expires; it never goes
 * past_due. Stripe needs a verified card; cash/manual and crypto count as an
 * arrangement once the banker committed to that rail during onboarding.
 */
export function resolveTrialOutcome(
  hasPaymentArrangement: boolean,
): Extract<SubscriptionStatus, 'active' | 'expired'> {
  return hasPaymentArrangement ? 'active' : 'expired';
}
